import {
  ANNOTATION_COMMENT_MAX_LENGTH,
  ANNOTATION_SUBMIT_MAX,
  type Annotation,
  type AnnotationShape,
  type ArtifactPreview,
  PREVIEW_POLL_INTERVAL_MS,
  PREVIEW_POLL_TIMEOUT_MS,
} from "@rakazo/contracts";
import { clipUtf16 } from "./utf16";

/**
 * A mark the reader is still drawing or commenting on. It lives only in the view until it is
 * saved, so reloading the server list never touches it.
 */
export type MarkupDraft = {
  page: number;
  shape: AnnotationShape;
  comment: string;
};

/** Annotations of one artifact family split into the shown version and older versions. */
export type SplitAnnotations = {
  /** Marks on the version in view, in page and creation order. */
  current: Annotation[];
  /** Marks on earlier versions, shown grey with their status. */
  older: Annotation[];
};

/** Splits the server list by version and sorts each part by page, then by creation time. */
export function splitAnnotations(
  list: readonly Annotation[],
  artifactId: string,
): SplitAnnotations {
  const byPage = (a: Annotation, b: Annotation) =>
    a.page - b.page || a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id);
  return {
    current: list.filter((a) => a.artifactId === artifactId).sort(byPage),
    older: list.filter((a) => a.artifactId !== artifactId).sort(byPage),
  };
}

/** Open marks of the shown version, preselected for "Umsetzen", at most the submit limit. */
export function defaultSelection(current: readonly Annotation[]): Set<string> {
  return new Set(
    current
      .filter((a) => a.status === "open")
      .slice(0, ANNOTATION_SUBMIT_MAX)
      .map((a) => a.id),
  );
}

/**
 * Keeps the reader's choice across a reload: ids that vanished or are done drop out, new open
 * marks join while there is room. Marks the reader unticked stay unticked.
 */
export function reconcileSelection(
  selected: ReadonlySet<string>,
  previous: readonly Annotation[],
  current: readonly Annotation[],
): Set<string> {
  const known = new Set(previous.map((a) => a.id));
  const next = new Set<string>();
  for (const a of current) {
    if (a.status !== "open") continue;
    if (selected.has(a.id)) next.add(a.id);
  }
  for (const a of current) {
    if (next.size >= ANNOTATION_SUBMIT_MAX) break;
    if (a.status === "open" && !known.has(a.id)) next.add(a.id);
  }
  return next;
}

/** Ticks or unticks one mark; ticking past the submit limit leaves the choice as it was. */
export function toggleSelection(selected: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(selected);
  if (next.has(id)) next.delete(id);
  else if (next.size < ANNOTATION_SUBMIT_MAX) next.add(id);
  return next;
}

/** Comment text as it may be saved: trimmed at the end and cut to the contract limit in UTF-16 units, as the schema counts. */
export function normalizeComment(text: string): string {
  return clipUtf16(text.trimEnd(), ANNOTATION_COMMENT_MAX_LENGTH);
}

/** Result of waiting for a preview: the last state seen and whether waiting ran out. */
export type PreviewPollResult = { preview: ArtifactPreview; timedOut: boolean };

/** Clock and timer used by pollPreview; injected so tests run without real waiting. */
export type PollClock = {
  now: () => number;
  sleep: (ms: number, signal?: AbortSignal) => Promise<void>;
};

const realClock: PollClock = {
  now: () => Date.now(),
  sleep: (ms, signal) =>
    new Promise((resolve, reject) => {
      const timer = setTimeout(resolve, ms);
      signal?.addEventListener(
        "abort",
        () => {
          clearTimeout(timer);
          reject(signal.reason);
        },
        { once: true },
      );
    }),
};

/**
 * Asks for the preview state until it is no longer pending: every PREVIEW_POLL_INTERVAL_MS, for
 * at most PREVIEW_POLL_TIMEOUT_MS. After that the view shows the preview as failed. Errors of the
 * fetch and an abort reach the caller unchanged.
 */
export async function pollPreview(
  fetchPreview: () => Promise<ArtifactPreview>,
  signal?: AbortSignal,
  clock: PollClock = realClock,
): Promise<PreviewPollResult> {
  const start = clock.now();
  let preview = await fetchPreview();
  while (preview.status === "pending") {
    if (clock.now() - start + PREVIEW_POLL_INTERVAL_MS > PREVIEW_POLL_TIMEOUT_MS) {
      return { preview: { status: "failed" }, timedOut: true };
    }
    await clock.sleep(PREVIEW_POLL_INTERVAL_MS, signal);
    signal?.throwIfAborted();
    preview = await fetchPreview();
  }
  return { preview, timedOut: false };
}
